import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { callFuncWith, choice, cloneDeep, firstNonEmpty, parseDec, unsub } from '@util/utils';
import { Observable, Subject } from 'rxjs';
import { DeleteDialogComponent, DeleteDialogData } from './delete/delete-dialog/delete-dialog.component';
import { EditDocumentComponent } from './edit/edit-document/edit-document.component';
import { FillAcceptanceDialogComponent } from './edit/fill-acceptance-dialog/fill-acceptance-dialog.component';
import { FillAgbDialogComponent } from './edit/fill-agb-dialog/fill-agb-dialog.component';
import { FillReceiptDialogComponent } from './edit/fill-receipt-dialog/fill-receipt-dialog.component';
import { FillReportDialogComponent } from './edit/fill-report-dialog/fill-report-dialog.component';
import { DeleteType, OrderDialogTypes } from './order-basic';
import { orderDialogPositions } from './order-dialog-positions';
import { DataService } from '@data/data.service';
import { NotificationService } from '@notification/notification.service';
import { BackendPaths } from '@common/backend-paths';
import { MyLog } from '@common/common';

export const enum ORDER_OPERATIONS {
  DELETED = 'deleted',
  DOCUMENT_DELETED = 'doc_deleted',
  DOCUMENT_RESTORED = 'doc_restored'
}

export interface OrderChangeEvent {
  type: string;
  id: string | number;
  success?:boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OrderDialogService {

  private orderChange = new Subject<OrderChangeEvent>();

  constructor(
    private matDialog: MatDialog,
    private dataService: DataService,
    private notificationService: NotificationService
  ) { }

  orderChanges(): Observable<OrderChangeEvent> {
    return this.orderChange.asObservable();
  }

  /**
   *
   * @param orderId ID des Auftrags
   * @param type Typ des Dokuments
   * @param content Inhalt (Blob)
   * @returns
   */
  openDialog(
    orderId: any,
    type: string,
    content: any) {

    const dialogData: any = {
      data: {
        docId: type,
        content,
        orderId
      },
      maxHeight: '95vh',
      maxWidth: '90vw',
      height: '95vh',
      position: {
        top: '2vh'
      }
    };
    const typeMapping: any = {
      [OrderDialogTypes.AGB]: FillAgbDialogComponent,
      [OrderDialogTypes.ACCEPTANCE]: FillAcceptanceDialogComponent,
      [OrderDialogTypes.RECEIPT]: FillReceiptDialogComponent,
      [OrderDialogTypes.REPORT]: FillReportDialogComponent
    }

    if (type in typeMapping) {
      dialogData.panelClass = 'my-document-dialog';

      return this.matDialog.open(
        typeMapping[type],
        dialogData
      ).afterClosed();
    }

    const docId = parseDec(type);

    if (Number.isInteger(docId)) {
      dialogData.panelClass = 'my-document-dialog';
      //delete dialogData.height;
      return this.matDialog.open(
        EditDocumentComponent,
        dialogData
      ).afterClosed();
    }
    return null;
  }

  tryRestoreOrder(orderId: any, cb?: (result: any) => void) {

    MyLog.log('RESTORE-ORDER:', orderId);

    const postData = {
      op: DeleteType.RESTORE_ORDER,
      order_id: orderId
    };
    const url = BackendPaths.TRASH_ORDERS_FULL;
    const ns = this.notificationService;

    this.dataService.post(url, postData).subscribe((res: any) => {
      const data = res?.result;
      if (data.success) {
        ns.showSuccess(choice(data.msg, 'Auftrag wiederhergestellt'));
      } else {
        const errs = firstNonEmpty(data.errors, [
          'Fehler auf dem Server.'
        ]);
        ns.showErrors(errs);
      }
      callFuncWith(cb, data);
    });
  }

  tryDeleteOrder(orderId: any, order: any, fromTrash = false, deleteCallback?: (result: any) => void) {
    const dialogRes: any = {
      sub: null
    };
    dialogRes.sub = this.matDialog.open(DeleteDialogComponent, {
      data: {
        type: DeleteType.ORDER,
        orderId: orderId,
        order: order,
        fromTrash:fromTrash
      } as DeleteDialogData
    }).afterClosed().subscribe((res: any) => {
      if (res && res.id) {
        this.orderChange.next({
          type: ORDER_OPERATIONS.DELETED,
          id: res.id
        });
      }

      callFuncWith(deleteCallback, res);

      dialogRes.sub = unsub(dialogRes.sub);
    });
  }

  tryRestoreDocument(orderId: any, docId: any, cb?: (result: any) => void) {

    MyLog.log('RESTORE-DOC:', orderId, docId);

    const postData = {
      op: DeleteType.RESTORE_DOCUMENT,
      order_id: orderId,
      document_id: docId
    };
    const url = BackendPaths.TRASH_DOCUMENTS_FULL;
    const ns = this.notificationService;

    this.dataService.post(url, postData).subscribe((res: any) => {
      const data = res?.result;
      if (data.success) {
        ns.showSuccess(choice(data.msg, 'Dokument wiederhergestellt'));
      } else {
        const errs = firstNonEmpty(data.errors, [
          'Fehler auf dem Server.'
        ]);
        ns.showErrors(errs);
      }

      callFuncWith(cb, data);
    });
  }

  tryDeleteDocument(fromTrash:boolean, orderId: any, documentId: any, name: any = '') {
    const dialogRes: any = {
      sub: null
    };
    dialogRes.sub = this.matDialog.open(DeleteDialogComponent, {
      data: {
        fromTrash:fromTrash,
        type: DeleteType.DOCUMENT,
        orderId: orderId,
        documentId: documentId,
        name: name
      } as DeleteDialogData
    }).afterClosed().subscribe((res: any) => {
      const changeRes: any = {
        type: ORDER_OPERATIONS.DOCUMENT_DELETED,
        success: false,
      }
      if (res && res.id) {
        changeRes.success = true;
        changeRes.id = res.id;
      } else {
        changeRes.cancelled = true;
      }
      this.orderChange.next(changeRes);

      dialogRes.sub = unsub(dialogRes.sub);
    });
  }

  getStylesForDialog(name: string, defaultStyle: any) {
    if (name && orderDialogPositions[name]) {
      return cloneDeep(orderDialogPositions[name]);
    }
    return defaultStyle;
  }
}
